import React, { useEffect } from 'react'
import { flowRight } from 'lodash'
import { useTranslation } from 'react-i18next'
import { withRouter } from 'react-router-dom'
import { Field } from 'formik'
import PropTypes from 'prop-types'
import { VEHICLE_SALES_METHOD } from 'lib/utils/store/vehicle'

import { withErrorBoundaryNoRender } from 'lib/errorBoundary'
import FormElementGroup from 'components/FormElements/FormElementGroup'
import Input from 'components/Forms/FormElements/Input'
import RadioGroup from 'components/Forms/FormElements/RadioGroup'
import TextArea from 'components/Forms/FormElements/TextArea'
import DateTimeInput from 'components/Forms/FormElements/DateTimeInput'

import './ad-status-sold-form.pcss'

const salesMethodOptions = [
    {
        value: VEHICLE_SALES_METHOD.DIRECT,
        name: 'Myynti',
    },
    {
        value: VEHICLE_SALES_METHOD.EXCHANGE,
        name: 'Vaihto',
    }
]

/**
 * AdStatusSoldForm Component
 *
 * This component renders a form used for marking an advertisement as sold, capturing details about the sale, including price, date, sales method, and additional comments.
 */
const AdStatusSoldForm = ({ focusFirst }) => {
    const { t } = useTranslation(['common', 'verticalAds'])

    useEffect(() => {
        focusFirst()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="ad-status-sold-form">
            <FormElementGroup cssClass="cols-2">
                <Field
                    name="price"
                    render={(props) => (
                        <Input
                            {...props}
                            label={`${t('verticalAds:form.sellPrice')} (€)`}
                            type="number"
                            pattern="[0-9]*"
                        />
                    )}
                />
                <Field
                    name="date"
                    type="text"
                    label={t('verticalAds:form.sellDate')}
                    dateFormat={false}
                    displayTime={false}
                    disableSetCurrentTime
                    component={DateTimeInput}
                />
            </FormElementGroup>
            <FormElementGroup cssClass="cols-1">
                <Field
                    name="salesMethod"
                    render={props => (
                        <RadioGroup
                            label={t('verticalAds:form.sellMethod')}
                            className="ad-sold-method"
                            options={salesMethodOptions}
                            {...props}
                        />
                    )}
                />
            </FormElementGroup>
            <FormElementGroup cssClass="cols-1">
                <Field
                    name="soldOwnComment"
                    render={props => (
                        <TextArea
                            label={t('verticalAds:form.sellOwnComment')}
                            placeholder={t('verticalAds:form.sellOwnCommentPlaceholder')}
                            rows="4"
                            {...props}
                        />
                    )}
                />
            </FormElementGroup>
            <FormElementGroup cssClass="cols-1">
                <Field
                    name="soldComment"
                    render={props => (
                        <TextArea
                            label={t('verticalAds:form.sellComment')}
                            placeholder={t('verticalAds:form.sellCommentPlaceholder')}
                            rows="4"
                            {...props}
                        />
                    )}
                />
            </FormElementGroup>
        </div>
    )
}

AdStatusSoldForm.propTypes = {
    setFieldValue: PropTypes.func.isRequired,
    focusFirst: PropTypes.func.isRequired,
}

export default flowRight(
    withErrorBoundaryNoRender,
    withRouter,
)(AdStatusSoldForm)
